import { useState } from 'react'

import { F0Dialog, F0Text } from '@factorialco/f0-react'

import {
  ArchiveProcurementTaskFormDocument,
  TaskFormsDataCollectionDocument,
  type TaskFormItemFragment,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface Props {
  taskForm: TaskFormItemFragment
  onClose: () => void
  onSuccess: () => void
}

const ArchiveTaskFormModal = ({ taskForm, onClose, onSuccess }: Props) => {
  const [loading, setLoading] = useState(false)

  const [archiveProcurementTaskForm] = useMutation(ArchiveProcurementTaskFormDocument, {
    extractorFn: (data) => data.procurementMutations.archiveProcurementTaskForm,
  })

  const handleConfirm = async () => {
    setLoading(true)

    const result = await archiveProcurementTaskForm({
      variables: { id: taskForm.id },
      awaitRefetchQueries: true,
      refetchQueries: [TaskFormsDataCollectionDocument],
    })

    const handled = handleResult(result, (errors) => {
      const message = Object.values(errors).join(', ') || i18n.t('errors.generic_error')
      app.setFlash({ type: 'wrong', message })
    })

    setLoading(false)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t(
          'procurement.purchase_requests.settings.task_forms.actions.archive_success'
        ),
      })
      onSuccess()
      onClose()
    }
  }

  return (
    <F0Dialog
      isOpen
      width="sm"
      onClose={onClose}
      title={i18n.t('procurement.purchase_requests.settings.task_forms.archive_modal.title')}
      primaryAction={{
        label: i18n.t('procurement.purchase_requests.settings.task_forms.archive_modal.confirm'),
        onClick: handleConfirm,
        loading,
        disabled: loading,
      }}
      secondaryAction={{
        label: i18n.t('procurement.purchase_requests.settings.task_forms.archive_modal.cancel'),
        onClick: onClose,
        disabled: loading,
      }}
    >
      <F0Text
        content={i18n.t(
          'procurement.purchase_requests.settings.task_forms.archive_modal.description',
          { name: taskForm.name }
        )}
        variant="body"
      />
    </F0Dialog>
  )
}

export default ArchiveTaskFormModal
